/**
 * One display name per organization and per person, so a store or contact reads
 * the same on every page. Display only: nothing stored is rewritten. Mirrors
 * `orgDisplayName` in `src/utils/orgDisplayName.ts` on the API.
 */

const clean = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** The name the store uses for itself: displayName, DBA, legal business name, then name. */
export function orgDisplayName(org?: Record<string, any> | null, fallback = ''): string {
  if (!org) return fallback;
  return (
    clean(org.displayName) ||
    clean(org.dba) ||
    clean(org.businessInfo?.dba) ||
    clean(org.businessName) ||
    clean(org.businessInfo?.businessName) ||
    clean(org.name) ||
    fallback
  );
}

interface PersonNameFields {
  displayName?: string | null;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/** A team member's name: the user profile first, then first + last, then the email handle. */
export function personDisplayName(person?: PersonNameFields | null, fallback = ''): string {
  if (!person) return fallback;
  const full = [clean(person.firstName), clean(person.lastName)].filter(Boolean).join(' ');
  return (
    clean(person.displayName) ||
    clean(person.name) ||
    full ||
    clean(person.email).split('@')[0] ||
    fallback
  );
}

/**
 * The organization's primary contact. The API resolves the member's profile name
 * as `primaryContactDisplayName`; the org's own contact field is the fallback.
 */
export function primaryContactName(org?: Record<string, any> | null, fallback = ''): string {
  if (!org) return fallback;
  return clean(org.primaryContactDisplayName) || clean(org.contactInfo?.primaryContact?.name) || fallback;
}
